import axios from 'axios'

// API base configuration
const API_BASE_URL = import.meta.env.VITE_API_URL

console.log('API_BASE_URL', API_BASE_URL)

if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is not set')
}

// Create axios instance with default config
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json',
    },
})

// Store for the current auth token getter function
let getAuthToken: (() => Promise<string | null>) | null = null

// Function to set the auth token getter (called from React components)
export const setAuthTokenGetter = (getter: () => Promise<string | null>) => {
    getAuthToken = getter
}

// Request interceptor for adding auth tokens
apiClient.interceptors.request.use(
    async (config) => {
        // Add Clerk JWT token if available
        if (getAuthToken) {
            try {
                const token = await getAuthToken()
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`
                    // TODO: Remove debug logging in production
                    console.log('🔐 JWT token attached to request:', config.url)
                } else {
                    console.log('🔓 No JWT token available for request:', config.url)
                }
            } catch (error) {
                console.error('Failed to get auth token:', error)
            }
        } else {
            console.log('🔓 No auth token getter available for request:', config.url)
        }
        return config
    },
    (error) => {
        return Promise.reject(error)
    }
)

// Response interceptor for error handling
apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        // Handle common errors
        if (error.response?.status === 401) {
            // Handle unauthorized - token might be expired
            console.warn('Unauthorized request - token may be expired')
        }
        return Promise.reject(error)
    }
)

// API endpoints object
export const api = {
    // Sightings endpoints
    sightings: {
        // Get all sightings with optional query params
        getAll: (params?: {
            page?: number
            limit?: number
            type?: string
            lat?: number
            lng?: number
            radius?: number
            // Coordinate bounds filtering - using correct parameter names
            latMin?: number
            latMax?: number
            lonMin?: number
            lonMax?: number
            // Category filtering - can be passed multiple times
            categoryIds?: string[]
        }) => apiClient.get('/sightings', { params }),

        // Get single sighting by ID
        getById: (id: string) => apiClient.get(`/sightings/${id}`),

        // Create new sighting
        create: (formData: FormData) => apiClient.post('/sightings', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }),

        // Update existing sighting
        update: (id: string, data: {
            name?: string
            description?: string
            categoryId?: string
            imageUrl?: string
            latitude?: number
            longitude?: number
        }) => apiClient.patch(`/sightings/${id}`, data),

        // Delete sighting
        delete: (id: string) => apiClient.delete(`/sightings/${id}`),

        // Get nearby sightings (if this endpoint exists)
        getNearby: (params: {
            lat: number
            lng: number
            radius?: number
            limit?: number
        }) => apiClient.get('/sightings/nearby', { params }),
    },

    // User endpoints (if authentication is needed)
    auth: {
        login: (credentials: { email: string; password: string }) =>
            apiClient.post('/auth/login', credentials),

        register: (userData: {
            email: string
            password: string
            name?: string
        }) => apiClient.post('/auth/register', userData),

        logout: () => apiClient.post('/auth/logout'),

        getProfile: () => apiClient.get('/auth/profile'),
    },

    // Categories/Types endpoints
    categories: {
        getAll: () => apiClient.get('/categories'),
        create: (data: { name: string; description?: string }) =>
            apiClient.post('/categories', data),
    },

    // Sighting Categories endpoints
    sightingCategories: {
        getAll: () => apiClient.get('/sighting-categories'),
        getById: (id: string) => apiClient.get(`/sighting-categories/${id}`),
        create: (data: { name: string }) =>
            apiClient.post('/sighting-categories', data),
    },

    // File upload endpoint
    upload: {
        image: (file: File) => {
            const formData = new FormData()
            formData.append('image', file)
            return apiClient.post('/upload/image', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            })
        },
    },
}

export default apiClient 